import React, { useEffect, useState, useContext, useRef } from 'react';
import {AnimatePresence, motion} from "framer-motion";

import { useParams, useNavigate  } from "react-router";
import { BuildPath } from  '@/helpers/Paths';
import { sortByTable } from  '@/interfaces/plannerSettings';
import { buildOverview, buildUsersAndGroups, checkHasOverviewChanged, checkHasUsersGroupsChanged, removeDuplicates, removeFromDisplay, tableOrder } from  '@/helpers/PlannerSettings';
import TableHeading from './TableHeading';
import SelectUsersOrUserGroupsModal, { ActiveTabs } from  '@/components/modals/SelectUsersOrUserGroupsModal';
import { IloadingType, PlannerSettingsContext } from '@/context/PlannerSettingsContext';
import SettingsSection from './SettingsSection';
import ModalWrapper from  '@/components/modals/ModalWrapper';
import PlannerUserGroupSearch from  '@/components/modals/PlannerUserGroupSearch';
import useHTTP from  '@/components/hooks/useHTTP';
import useSelectedCalendar from  '@/components/hooks/useSelectedCalendar';
import TableVal from './TableVal';
import { ToastData } from  '@/interfaces/LockerDetails';
import ToastNotification, { ToastMessageType } from '../global/ToastNotification';
import usePlannerSettings from  '@/components/hooks/usePlannerSettings';

export interface IAdmin {
    id:number, 
    name:string, 
    jobTitle:string,
    department:string
}

export interface IEditor {
    id:number, 
    name:string, 
    jobTitle:string,
    department:string
}

export interface IUserGroup {
    id:number,
    groupName:string,
    totalUsersInGroup:number,
    lastUpdated:string,
}

export interface IUser {
    id:number,
    department:string,
	name: string,
    firstName: string,
    lastName: string,
    jobRole: string
}

enum section {
    calendarOverview = "calendarOverview",
    userGroups = "userGroups",
    settings = "settings"
}

const EditCalendar = () => {

    const [loadingOverview, setLoadingOverview] = useState<boolean>(null)
    const [loadingUsersGroups, setLoadingUsersGroups] = useState<boolean>(null)

    const [selectedTab, setSelectedTab] = useState<section>(section.calendarOverview);
    const [formTouched, setFormTouched] = useState<boolean>(false);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const [showGroupsUsersModal, setShowGroupsUsersModal] = useState<boolean>(false);
    const [onConfirmMoveTab, setOnConfirmMoveTab] = useState<section|null>();
    const [toast, setToast] = useState<ToastData>({show: false, message: undefined, type: undefined});

    // Overview
    const [admins, setAdmins] = useState<IAdmin[]>([]);
    const [adminsOrder, setAdminsOrder] = useState<sortByTable>({field:"", order:null, fieldType:null});
    const [editors, setEditors] = useState<IEditor[]>([]);
    const [editorsOrder, setEditorsOrder] = useState<sortByTable>({field:"", order:null, fieldType:null});
    const orginalAdmins = useRef<IAdmin[]>([]);
    const orginalEditors = useRef<IEditor[]>([]);
    const isAdminMoalSelected = useRef<boolean>(null);

    // Users & groups
    const [userGroups, setUserGroups] = useState<IUserGroup[]>([]);
    const [selectedUserGroup, setSelectedUserGroup] = useState<IUserGroup>(null);
    const [userGroupsOrder, setUserGroupsOrder] = useState<sortByTable>({field:"", order:null, fieldType:null});
    const [users, setUsers] = useState<IUser[]>([]);
    const [usersOrder, setUsersOrder] = useState<sortByTable>({field:"", order:null, fieldType:null});
    const orginalUsers = useRef<IUser[]>([]);
    const orginalGroups = useRef<IUserGroup[]>([]);
    
    // Modals
    const [showEditGroupsModal, setShowEditGroupsModal] = useState<boolean>(false);
    const [showEditUsersModal, setShowEditUsersModal] = useState<boolean>(false);
    const [showAdminModal, setShowAdminModal] = useState<boolean>(false);
    const [showEditorsModal, setShowEditorsModal] = useState<boolean>(false);

    const {selectedUsers, setSelectedUsers, selectedGroups, setSelectedGroups, permissions, setLoading, setLoadingType} = useContext(PlannerSettingsContext);

    const selectedCalendar = useSelectedCalendar();

    const navigate = useNavigate();
    const { get, post } = useHTTP();

    let { id } = useParams();

    const {loadingPerm} = usePlannerSettings(Number(id))  

    const userGroupsSaveRef = useRef<HTMLButtonElement>(null);
    const overviewSaveRef = useRef<HTMLButtonElement>(null);

    // Init
    useEffect(() => {

        if (!id) return

        if (selectedTab === section.userGroups){
            getUsersAndGroups()
        }
        else if (selectedTab === section.calendarOverview){
            getOverview()
        }

    }, [id, selectedTab])

    // Move into main
    useEffect(() => {

        // Remove from main when un-selected in modal
        const removed = removeFromDisplay(selectedGroups, userGroups)

        const dups = removeDuplicates(removed, selectedGroups)

        setUserGroups(dups)

    }, [selectedGroups])

    useEffect(() => {

        if (selectedTab === section.calendarOverview){

            // Work out which modal was selected 
            if (isAdminMoalSelected.current){
                
                // Remove from main when un-selected in modal
                const removedAdmins = removeFromDisplay(selectedUsers, admins)

                // Check if duplicated (adding)
                setAdmins(removeDuplicates(removedAdmins, selectedUsers))  

            }
            else{
                const removedEditors = removeFromDisplay(selectedUsers, editors)

                setEditors(removeDuplicates(removedEditors, selectedUsers))  
            }
        }
        else if (selectedTab === section.userGroups){

            const removed = removeFromDisplay(selectedUsers, users)

            setUsers(removeDuplicates(removed, selectedUsers))   
        }


    }, [selectedUsers])

    // Overview
    useEffect(() => {

        if (adminsOrder.order && adminsOrder.field){
            setAdmins((admins) => tableOrder(admins, adminsOrder))
        }

    }, [adminsOrder])

    useEffect(() => {

        if (editorsOrder.order && editorsOrder.field){
 
            setEditors((editors) => tableOrder(editors, editorsOrder))
        }

    }, [editorsOrder])

    // Users & groups
    useEffect(() => {

        if (userGroupsOrder.order && userGroupsOrder.field){
            setUserGroups(tableOrder(userGroups, userGroupsOrder))
        }

    }, [userGroupsOrder])

    useEffect(() => {

        if (usersOrder.order && usersOrder.field){
            setUsers(tableOrder(users, usersOrder))
        }

    }, [usersOrder])

    // Handle multiple loadings
    useEffect(() => {

        setLoadingType(IloadingType.loading)

        if (loadingPerm || loadingOverview){
            setLoading(true)
        }
        else if(!loadingPerm && !loadingOverview ){
            setLoading(false)
        }

    }, [loadingPerm, loadingOverview])

    useEffect(() => {

        setLoadingType(IloadingType.loading)

        if (loadingPerm || loadingUsersGroups){
            setLoading(true)
        }
        else if (!loadingPerm && !loadingUsersGroups){
            setLoading(false)
        }

    }, [loadingPerm, loadingUsersGroups])

    // Overview check if form altered
    useEffect(() => {

        if (selectedTab ===  section.calendarOverview){
            const isChanged = checkHasOverviewChanged(admins, editors, orginalAdmins.current, orginalEditors.current);

            setFormTouched(isChanged)
        }

    }, [admins, editors, selectedTab])

    // Users & groups check if form altered
    useEffect(() => {

        if (selectedTab === section.userGroups){
            const isChanged = checkHasUsersGroupsChanged(selectedUsers, selectedGroups, orginalUsers.current, orginalGroups.current);

            setFormTouched(isChanged)
        }

    }, [selectedUsers, selectedGroups, selectedTab])


    function getOverview(){

        setLoadingOverview(true)

        get(`planner/calendar/${id}/permissions?roleNames=Admin&roleNames=Manager`).then(({data}) => {

            // Admins
            const admins = data.users.filter(x => x.roleName === "Admin")
            setAdmins(admins)
            orginalAdmins.current = admins

            // Editors
            const editors = data.users.filter(x => x.roleName === "Manager")
            setEditors(editors)
            orginalEditors.current = editors
    
            setFormTouched(false)

        }).finally(() => {

            setLoadingOverview(false)
        })
    }

    function saveOverview(){

 
        if (overviewSaveRef.current){
            overviewSaveRef.current.setAttribute("disabled", "disabled");
        }

        const allUsers = buildOverview(admins, editors, orginalAdmins.current, orginalEditors.current);

        if (allUsers.length && id){

            post(`planner/calendar/${id}/permissions`, allUsers)
            .then(() => {

                setToast({
                    type: ToastMessageType.SUCCESS,
                    message: 'Overview saved',
                    show: true
                })

            })
            .catch((err) => {
                console.log(err)

                setToast({
                    type: ToastMessageType.WARNING,
                    message: 'Error',
                    show: true
                })

            })
            .finally(() => {

                // Refresh
                getOverview()
            })
        }
    }

    function getUsersAndGroups(){

        // User groups
        setLoadingUsersGroups(true)
        get(`planner/calendar/${id}/permissions?roleNames=User`).then(({data}) => {

            // Groups
            setUserGroups(data.groups)
            setSelectedGroups(data.groups)
            orginalGroups.current = data.groups

            // Users
            setUsers(data.users)
            setSelectedUsers(data.users)
            orginalUsers.current = data.users

            setFormTouched(false)
        })
        .finally(() => {
            setLoadingUsersGroups(false)
        })
    }

    function saveUserGroups(){

        if (userGroupsSaveRef.current){
            userGroupsSaveRef.current.setAttribute("disabled", "disabled");
        }

        const allUsers = buildUsersAndGroups(selectedUsers, selectedGroups, orginalUsers.current, orginalGroups.current);

        if (allUsers.length && id){

            post(`planner/calendar/${id}/permissions`, allUsers)
            .then(() => {

                setToast({
                    type: ToastMessageType.SUCCESS,
                    message: 'Users & groups saved',
                    show: true
                })

            })
            .catch(() => {
                
                setToast({
                    type: ToastMessageType.WARNING,
                    message: 'Error',
                    show: true
                })

            })
            .finally(() => {
                
                // Refresh
                getUsersAndGroups()
            })
        }
    }

    function moveAway(to:section){

        if (to === selectedTab) return

        if (formTouched){
            setShowConfirmModal(true);
            setOnConfirmMoveTab(to);
        }
        else{
            // Clear modals
            setSelectedUsers([])
            setSelectedGroups([])
            setSelectedTab(to);
        }
    }

    function cancel(){
        if (formTouched){
            setShowConfirmModal(true)
            setOnConfirmMoveTab(null)
        }
        else{
            navigate("/#")
        }
    }

    function discard(){
        setFormTouched(false);
        setShowConfirmModal(false);
        
        // Clear modals
        setSelectedUsers([])
        setSelectedGroups([])
        
        if (onConfirmMoveTab){
            setSelectedTab(onConfirmMoveTab)
        }
        else{
            navigate("/#")
        }
    }

    function filterSelectedUsersOverview(isAdmin:boolean){

        // Both sections on overview tab work with users so have to clear/apply on admin or editor modal selection
        if (isAdmin){
            isAdminMoalSelected.current = true;
            setSelectedUsers(admins)
            setShowAdminModal(true)
        }
        else{
            isAdminMoalSelected.current = false;
            setSelectedUsers(editors)
            setShowEditorsModal(true)
        }
    }

    if (!selectedCalendar){
        return null
    }

    return (
        <div className='planner-settings edit-calendar'>
                
            <div className='title-container main-title'>

                <h2 className='title'>{selectedCalendar.name}</h2>       

            </div>

            <div className='tabs'>

                <div className='tabs-header'>
                    <span onClick={() => moveAway(section.calendarOverview)} className={`${selectedTab === section.calendarOverview ? 'selected': ''}`}>Calendar Overview</span>
                    <span onClick={() => moveAway(section.userGroups)} className={`${selectedTab === section.userGroups ? 'selected': ''}`}>Users & User Groups</span>
                    
                    {permissions.canManageCalendar && 
                        <span onClick={() => moveAway(section.settings)} className={`${selectedTab === section.settings ? 'selected': ''}`}>Settings</span>
                    }
                </div>
                <AnimatePresence mode="wait">
                    {selectedTab === section.calendarOverview && (
                        <motion.div
                            key="calendar-overview"
                            className='tab-content'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className='tab-content'>

                                <div className='planner-settings-content'>

                                    <div className='table-container spacer'>

                                        <div className='table-title-container'>
                                            <h2 className='title'>Admins</h2>

                                            {permissions.canManageCalendar &&
                                                <span className='user-modal-text' onClick={() => filterSelectedUsersOverview(true)}>Manage Admins</span>
                                            }
                                        </div>

                                        <p>Calendar Admins can create and manage events, assign users and groups to the calendar, and manage calendar details. Org Admins have full access by default and do not need to be added.</p>

                                        <table className="listing-table editors-table" cellPadding="0" cellSpacing="0">

                                            <thead>
                                            <tr className='heading'>
                                                <TableHeading order={adminsOrder} setOrder={setAdminsOrder} field="name" label="Name" />
                                                <TableHeading order={adminsOrder} setOrder={setAdminsOrder} field="jobTitle" label="Job Role" />
                                                <TableHeading order={adminsOrder} setOrder={setAdminsOrder} field="department" label="Department" />
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {admins.map(item => {
                                                return (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <TableVal text={item.name} />
                                                        </td>

                                                        <td>
                                                            <TableVal text={item.jobTitle} />
                                                        </td>

                                                        <td>
                                                            <TableVal text={item.department} />
                                                        </td>
                                                    </tr>
                                                )
                                            })}

                                            {admins.length === 0 &&
                                                <tr>
                                                    <td colSpan={3} className="listing-table-empty">No admins added</td>
                                                </tr>
                                            }
                                            </tbody>

                                        </table>
                                    </div>

                                    {showAdminModal &&
                                        <SelectUsersOrUserGroupsModal
                                            show={true}
                                            onClose={() => setShowAdminModal(false)}
                                            className="select-users-or-user-groups-modal"
                                            defaultTab={ActiveTabs.USERS}
                                            setDefaultTab={() => {}}
                                            planner={true}
                                            disabledTabs={[ActiveTabs.GROUPS, ActiveTabs.SELECTEDGROUPS]}
                                            filterParams={{ExcludeOrgAdmins:true}}
                                        />
                                    }

                                    <div className='table-container'>

                                        <div className='table-title-container'>
                                            <h2 className='title'>Editors</h2>

                                            {permissions.canManageCalendar &&
                                                <span className='user-modal-text' onClick={() => filterSelectedUsersOverview(false)}>Manage Editors</span>
                                            }
                                        </div>

                                        <p>Calendar Editors can create and manage events, and invite users and user groups assigned to that calendar.</p>

                                        <table className="listing-table editors-table" cellPadding="0" cellSpacing="0">

                                            <thead>
                                            <tr className='heading'>
                                                <TableHeading order={editorsOrder} setOrder={setEditorsOrder} field="name" label="Name" />
                                                <TableHeading order={editorsOrder} setOrder={setEditorsOrder} field="jobTitle" label="Job Role" />
                                                <TableHeading order={editorsOrder} setOrder={setEditorsOrder} field="department" label="Department" />
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {editors.map((item, idx) => {

                                                return (
                                                    <tr key={idx}>
                                                        <td>
                                                            <TableVal text={item.name} />
                                                        </td>

                                                        <td>
                                                            <TableVal text={item.jobTitle} />
                                                        </td>

                                                        <td>
                                                            <TableVal text={item.department} />
                                                        </td>
                                                    </tr>
                                                )

                                            })}

                                            {editors.length === 0 &&
                                                <tr>
                                                    <td colSpan={3} className="listing-table-empty">No editors added</td>
                                                </tr>
                                            }
                                            </tbody>

                                        </table>
                                    </div>

                                    {showEditorsModal &&
                                        <SelectUsersOrUserGroupsModal
                                            show={true}
                                            onClose={() => setShowEditorsModal(false)}
                                            className="select-users-or-user-groups-modal"
                                            defaultTab={ActiveTabs.USERS}
                                            setDefaultTab={() => {}}
                                            planner={true}
                                            disabledTabs={[ActiveTabs.GROUPS, ActiveTabs.SELECTEDGROUPS]}
                                            filterParams={{ExcludeOrgAdmins:true}}
                                        />
                                    }

                                </div>

                                <div className='button-container'>
                                    <button className='cancel' onClick={() => cancel()}>Cancel</button>
                                    {permissions.canManageCalendar && <button className='save' ref={overviewSaveRef} onClick={saveOverview} disabled={!formTouched}>Save Changes</button> }
                                </div>

                            </div>
                        </motion.div>
                    )}
                    {selectedTab === section.userGroups && (
                        <motion.div
                            key="user-groups"
                            className='tab-content'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <div className='tab-content'>

                                <div className='planner-settings-content'>

                                    <h2 className='title'>Users & User Groups</h2>

                                    <p>These are the users and user groups assigned to this calendar. When you or another calendar editor creates an event, these are the people available to be invited. If there are no groups or users listed below, you will be able to invite all users across your organisation to your events. If you feel something is not quite right please contact your workspace adminstrator.</p>

                                    <div className='table-container spacer'>

                                        <div className='table-title-container'>
                                            <h2 className='title'>User Groups</h2>

                                            {permissions.canManageCalendar &&
                                                <span className='user-modal-text' onClick={() => setShowEditGroupsModal(true)}>Edit Groups</span>
                                            }
                                        </div>

                                        <table className="listing-table user-groups-table" cellPadding="0" cellSpacing="0">

                                            <thead>
                                            <tr className='heading'>

                                                <TableHeading order={userGroupsOrder} setOrder={setUserGroupsOrder} field="groupName" label="Name" />

                                                <TableHeading order={userGroupsOrder} setOrder={setUserGroupsOrder} field="totalUsersInGroup" label="Active Users" />

                                            </tr>
                                            </thead>

                                            <tbody>
                                            {userGroups.map((item) => {
                                                return (
                                                    <tr key={item.id}>
                                                        <td className="img-link" >

                                                            {!permissions.canAddCalendar && item.groupName}

                                                            {permissions.canAddCalendar &&
                                                                <a href={`/admin/OrganizationUserGroup/UserGroupDetail/${item.id}`} target="_blank" rel="noreferrer" className="group-link">{item.groupName}</a>
                                                            }

                                                            <img className="eye" src={`${BuildPath()}/images/planner/eye.svg`} alt="" onClick={() => {setShowGroupsUsersModal(true);setSelectedUserGroup(item)}} />
                                                        </td>
                                                        <td>
                                                            {item.totalUsersInGroup}
                                                        </td>
                                                    </tr>
                                                )
                                            })}

                                            {userGroups.length === 0 &&
                                                <tr>
                                                    <td colSpan={2} className="listing-table-empty">No user groups added</td>
                                                </tr>
                                            }
                                            </tbody>

                                        </table>
                                    </div>

                                    {showEditGroupsModal &&
                                        <SelectUsersOrUserGroupsModal
                                            show={true}
                                            onClose={() => setShowEditGroupsModal(false)}
                                            className="select-users-or-user-groups-modal"
                                            defaultTab={ActiveTabs.GROUPS}
                                            setDefaultTab={() => {}}
                                            planner={true}
                                            disabledTabs={[ActiveTabs.USERS, ActiveTabs.SELECTEDUSERS]}
                                        />
                                    }

                                    <div className='table-container'>

                                        <div className='table-title-container'>
                                            <h2 className='title'>Users</h2>

                                            {permissions.canManageCalendar &&
                                                <span className='user-modal-text' onClick={() => setShowEditUsersModal(true)}>Edit Users</span>
                                            }
                                        </div>

                                        <table className="listing-table users-table" cellPadding="0" cellSpacing="0">

                                            <thead>
                                            <tr className='heading'>
                                                <TableHeading order={usersOrder} setOrder={setUsersOrder} field="name" label="Username" />
                                                <TableHeading order={usersOrder} setOrder={setUsersOrder} field="firstName" label="First Name" />
                                                <TableHeading order={usersOrder} setOrder={setUsersOrder} field="lastName" label="Last Name" />
                                                <TableHeading order={usersOrder} setOrder={setUsersOrder} field="department" label="Department" />
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {users.map(item => {
                                                return (
                                                    <tr key={item.id}>
                                                        <td>
                                                            <TableVal text={item.name} />
                                                        </td>
                                                        <td>
                                                            <TableVal text={item.firstName} />
                                                        </td>
                                                        <td>
                                                            <TableVal text={item.lastName} />
                                                        </td>
                                                        <td>
                                                            <TableVal text={item.department} />
                                                        </td>
                                                    </tr>
                                                )
                                            })}

                                            {users.length === 0 &&
                                                <tr>
                                                    <td colSpan={4} className="listing-table-empty">No users added</td>
                                                </tr>
                                            }
                                            </tbody>

                                        </table>

                                    </div>

                                    {showEditUsersModal &&
                                        <SelectUsersOrUserGroupsModal
                                            show={true}
                                            onClose={() => setShowEditUsersModal(false)}
                                            className="select-users-or-user-groups-modal"
                                            defaultTab={ActiveTabs.USERS}
                                            setDefaultTab={() => {}}
                                            planner={true}
                                            disabledTabs={[ActiveTabs.GROUPS, ActiveTabs.SELECTEDGROUPS]}
                                        />
                                    }

                                </div>

                                <div className='button-container'>
                                    <button className='cancel' onClick={() => cancel()}>Cancel</button>
                                    {permissions.canManageCalendar && <button className='save' ref={userGroupsSaveRef} onClick={saveUserGroups} disabled={!formTouched}>Save Changes</button> }
                                </div>

                            </div>
                        </motion.div>
                    )}
                    {selectedTab === section.settings && (

                        <motion.div
                            key="settings"
                            className='tab-content'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <SettingsSection add={false} />
                        </motion.div>
                    )}

                </AnimatePresence>
            </div>

            <ModalWrapper hideCloseButton={true} isOpen={showConfirmModal} onRequestClose={() => setShowConfirmModal(false)} className="setting-confirm-modal">
                <p>You have unsaved changes, are you sure you want to leave this tab?</p>

                <div className='confirm-buttons'>
                    <button className='return' onClick={() => setShowConfirmModal(false)}>Return</button>
                    <button className='discard' onClick={() => discard()}>Discard Changes</button>
                </div>
            </ModalWrapper>

            <ModalWrapper isOpen={showGroupsUsersModal} onRequestClose={() => setShowGroupsUsersModal(false)} className="setting-users-groups-modal">            
                <PlannerUserGroupSearch group={selectedUserGroup} />
            </ModalWrapper>

            <ToastNotification show={toast.show} closeToast={() => setToast({...toast, show: false})} messageType={toast?.type}>
                {toast?.message}
            </ToastNotification>


        </div>
    );
};

export default EditCalendar;
